import type {
    ClearArgs,
    DrawArgs,
    HalImageChannelLayout,
    HalImageDataType,
    HalImagePhysical,
    HalImageStats,
    NativeImageData,
    PartialHalImageDescriptor,
    ReadImageDataArgs,
    TypedArrayImage,
    ViewTransformArgs,
    WriteImageDataArgs,
} from "@cm/hal"
import {
    completeHalImageDescriptor,
    createTypedArrayImage,
    getBytesPerChannel,
    getMipLevelSize,
    getNumChannels,
    getNumMipLevels,
    getSourceDataSize,
    isHalImage,
    isNativeImageData,
    isTypedArrayImage,
} from "@cm/hal"
import type {WebGl2Context} from "#webgl2/webgl2-context"
import type {WebGl2ImageDescriptor} from "#webgl2/webgl2-image"
import type {Box2Like, Size2Like} from "@cm/math"
import {Box2, Matrix3x2, Size2, Vector2} from "@cm/math"
import {assertNever} from "@cm/utils"
import {WebGl2ImageBase} from "#webgl2/webgl2-image-base"
import {checkForGlError} from "#webgl2/helpers/gl-error"
import {extractSubregionImageData, extractSubregionTypedArray, getGlFormatAndType, getInternalFormat} from "#webgl2/webgl2-image-utils"
import {createCroppedImageBitmap, isFirefox} from "@cm/browser-utils"
import {WebGl2Settings} from "#webgl2/settings"
import {loggerFactory} from "@cm/browser-log"

const FRAME_BUDGET_MS = 8 // yield to browser after this many ms of upload work

export class WebGl2ImagePhysical extends WebGl2ImageBase implements HalImagePhysical {
    readonly isHalImagePhysical = true

    readonly forceAlphaToOne: boolean

    constructor(
        readonly context: WebGl2Context,
        descriptor: PartialHalImageDescriptor,
    ) {
        super()
        WebGl2ImagePhysical.instances?.add(this)
        this.forceAlphaToOne = descriptor.channelLayout === "RGB"
        const {descriptor: webGl2Descriptor, internalChannelLayout, internalDataType, texture, videoMemSizeInBytes} = this.createTexture(descriptor)
        this._descriptor = webGl2Descriptor
        this._internalChannelLayout = internalChannelLayout
        this._internalDataType = internalDataType
        this._texture = texture
        // Images that will never be used as a paint/blit render target don't need framebuffers - skip allocating
        // them entirely. beginDrawPass's existing bounds check on _frameBuffers.length (below) already throws
        // loudly ("Invalid frame buffer index") if something mistakenly tries to paint into such an image.
        this._frameBuffers = descriptor.noRenderTarget ? [] : this.createFrameBuffers(this._descriptor, this._texture)
        this.stats = {
            videoMemSizeInBytes,
        }
    }

    // HalEntity
    dispose(): void {
        if (this._isDisposed) {
            throw Error("Image already disposed.")
        }
        this._isDisposed = true
        if (this._drawCount > 0) {
            logger.warn(
                `WebGl2ImagePhysical: Disposing an image that has been drawn ${this._drawCount} times. This may lead to memory leaks if the image is not properly disposed after use.`,
            )
        }
        logger.trace(
            `Disposing physical WebGL texture with ${this._descriptor.numShards.x}x${this._descriptor.numShards.y} shards of size ${this._descriptor.shardSize.width}x${this._descriptor.shardSize.height} containing ${this._descriptor.numMipLevels} mipmap levels for image of size ${this._descriptor.width}x${this._descriptor.height}.`,
        )
        const gl = this.context.gl
        this._frameBuffers.forEach((frameBuffer) => gl.deleteFramebuffer(frameBuffer))
        this._frameBuffers.length = 0
        gl.deleteTexture(this._texture)
        WebGl2ImagePhysical.instances?.delete(this)
    }

    // HalImage
    get descriptor(): WebGl2ImageDescriptor {
        return this._descriptor
    }

    get texture(): WebGLTexture {
        return this._texture
    }

    // HalPaintable
    beginDraw(_args: DrawArgs): number {
        return this._descriptor.numShards.x * this._descriptor.numShards.y
    }

    // HalPaintable
    beginDrawPass(args: DrawArgs, pass: number): ViewTransformArgs {
        if (!this._frameBuffers) {
            throw Error("Frame buffers not initialized")
        }
        const gl = this.context.gl
        const renderTargetShardIndex = pass
        // this._isModified = true // we assume that the image will be modified

        const shardRegion = new Box2(
            (renderTargetShardIndex % this._descriptor.numShards.x) * this._descriptor.shardSize.width,
            Math.floor(renderTargetShardIndex / this._descriptor.numShards.x) * this._descriptor.shardSize.height,
            this._descriptor.shardSize.width,
            this._descriptor.shardSize.height,
        )
        const regionIntersection = Box2.intersect(shardRegion, args.clipRegion)
        const frameBufferIndex = this.computeFrameBufferIndex(renderTargetShardIndex, args.layer, args.mipLevel)
        if (frameBufferIndex < 0 || frameBufferIndex >= this._frameBuffers.length) {
            throw Error("Invalid frame buffer index")
        }
        const frameBuffer = this._frameBuffers[frameBufferIndex]
        if (!frameBuffer) {
            throw Error("Frame buffer not found")
        }
        gl.bindFramebuffer(gl.FRAMEBUFFER, frameBuffer)
        gl.viewport(regionIntersection.x - shardRegion.x, regionIntersection.y - shardRegion.y, regionIntersection.width, regionIntersection.height)
        const transform = new Matrix3x2([1, 0, 0, 1, -1, -1]) // origin at top left
        transform.append(new Matrix3x2([2 / regionIntersection.width, 0, 0, 2 / regionIntersection.height, 0, 0])) // normalize to [-1, 1]
        transform.append(
            new Matrix3x2([
                1,
                0,
                0,
                1,
                -Math.max(0, regionIntersection.x) + args.transformOffset.x,
                -Math.max(0, regionIntersection.y) + args.transformOffset.y,
            ]),
        ) // offset to shard
        return {
            viewTransform: transform,
            uvOffset: args.uvTransformOffset,
        }
    }

    // HalPaintable
    endDrawPass(_args: DrawArgs, _pass: number): void {}

    // HalPaintable
    endDraw(_args: DrawArgs): void {
        const gl = this.context.gl
        gl.bindFramebuffer(gl.FRAMEBUFFER, null) // unbind framebuffer to avoid potential subsequent feedback framebuffer operation
    }

    // HalPaintable
    get width(): number {
        return this._descriptor.width
    }

    // HalPaintable
    get height(): number {
        return this._descriptor.height
    }

    // HalPaintable
    clear(args?: ClearArgs) {
        this.context.fill({
            target: this,
            targetRegion: args?.region,
            targetMipLevel: args?.mipLevel,
            color: args?.color ?? {r: 0, g: 0, b: 0, a: 0},
        })
    }

    // HalImage
    getMipLevelSize(mipLevel: number): Size2Like {
        if (mipLevel < 0 || mipLevel >= this._descriptor.numMipLevels) {
            throw Error("Invalid mip level.")
        }
        return getMipLevelSize(this._descriptor, mipLevel)
    }

    // HalImage
    readImageDataFloat(args?: ReadImageDataArgs): Float32Array {
        const mipLevel = args?.mipLevel ?? 0
        if (mipLevel !== 0) {
            throw new Error("Only mip-level 0 is supported at the moment")
        }
        const region = args?.region ?? {x: 0, y: 0, width: this._descriptor.width, height: this._descriptor.height}
        if (
            region.x < 0 ||
            region.y < 0 ||
            region.width < 0 ||
            region.height < 0 ||
            region.x + region.width > this._descriptor.width ||
            region.y + region.height > this._descriptor.height
        ) {
            throw Error("Invalid region.")
        }
        const numChannels = getNumChannels(this._descriptor.channelLayout)
        const numElements = region.width * region.height * numChannels
        const imageData = new Float32Array(numElements)
        const gl = this.context.gl
        const sourceShardImage =
            this._descriptor.dataType !== "float32" || this._descriptor.channelLayout !== "RGBA"
                ? this.context.createPhysicalImage({
                      width: Math.min(region.width, this._descriptor.shardSize.width),
                      height: Math.min(region.height, this._descriptor.shardSize.height),
                      channelLayout: "RGBA",
                      dataType: "float32",
                  })
                : null
        gl.bindTexture(gl.TEXTURE_2D_ARRAY, this._texture)
        const readFbo = gl.createFramebuffer()
        gl.pixelStorei(gl.PACK_ALIGNMENT, 1) // make sure to tightly pack the data

        // move the data from the gpu
        const shardSize = Vector2.fromSize2Like(this._descriptor.shardSize)
        const minShard = Vector2.fromVector2Like(region).divInPlace(shardSize).floorInPlace()
        const maxShard = Vector2.fromVector2Like(region).addInPlace(Vector2.fromSize2Like(region)).divInPlace(shardSize).ceilInPlace()
        const isMultiShard = maxShard.x - minShard.x > 1 || maxShard.y - minShard.y > 1
        const shardRegionSize = new Size2(Math.min(shardSize.x, region.width), Math.min(shardSize.y, region.height))
        const isRGBA = numChannels === 4
        const needsShardRegionData = isMultiShard || !!sourceShardImage
        let shardRegionData: Float32Array | undefined = sourceShardImage
            ? undefined
            : needsShardRegionData
              ? new Float32Array(shardRegionSize.width * shardRegionSize.height * 4)
              : imageData
        for (let sy = minShard.y; sy < maxShard.y; sy++) {
            for (let sx = minShard.x; sx < maxShard.x; sx++) {
                const shardIndex = sy * this._descriptor.numShards.x + sx
                const shardRegion = {
                    x: sx * shardSize.x,
                    y: sy * shardSize.y,
                    width: shardSize.x,
                    height: shardSize.y,
                }
                const intersectRegion = Box2.intersect(shardRegion, region)
                if (sourceShardImage) {
                    this.context.blit({
                        sourceImage: this,
                        sourceRegion: intersectRegion,
                        targetImage: sourceShardImage.ref,
                    })
                    shardRegionData = sourceShardImage.ref.readImageDataFloat({
                        region: {x: 0, y: 0, width: intersectRegion.width, height: intersectRegion.height},
                    })
                } else {
                    if (!shardRegionData) {
                        throw Error("Internal error: shardRegionData is not initialized.")
                    }
                    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, readFbo)
                    gl.framebufferTextureLayer(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, this._texture, 0, shardIndex)
                    gl.readPixels(intersectRegion.x, intersectRegion.y, intersectRegion.width, intersectRegion.height, gl.RGBA, gl.FLOAT, shardRegionData) // readPixels only supports RGBA
                    checkForGlError(gl, "Reading texture data")
                }
                if (needsShardRegionData) {
                    if (!shardRegionData) {
                        throw Error("Internal error: shardRegionData is not initialized.")
                    }
                    // move region data to image data
                    if (isRGBA) {
                        for (let row = 0; row < intersectRegion.height; row++) {
                            const srcStart = row * intersectRegion.width * numChannels
                            const srcEnd = srcStart + intersectRegion.width * numChannels
                            const dstStart = ((intersectRegion.y - region.y + row) * region.width + intersectRegion.x - region.x) * numChannels
                            imageData.set(shardRegionData.subarray(srcStart, srcEnd), dstStart)
                        }
                    } else {
                        for (let row = 0; row < intersectRegion.height; row++) {
                            const srcStart = row * intersectRegion.width * 4
                            const dstStart = ((intersectRegion.y - region.y + row) * region.width + intersectRegion.x - region.x) * numChannels
                            for (let x = 0; x < intersectRegion.width; x++) {
                                for (let c = 0; c < numChannels; c++) {
                                    imageData[dstStart + x * numChannels + c] = shardRegionData[srcStart + x * 4 + c]
                                }
                            }
                        }
                    }
                }
            }
        }

        sourceShardImage?.release()
        gl.deleteFramebuffer(readFbo)
        return imageData
    }

    private _drawCount = 0

    // HalImage
    async writeImageData({sourceData, sourceRegion, sourceMipLevel, targetOffset, targetLayer, targetMipLevel, maxPixelsPerUpload}: WriteImageDataArgs) {
        sourceRegion ??= {x: 0, y: 0, ...getSourceDataSize(sourceData)}
        sourceMipLevel ??= 0
        targetOffset ??= {x: 0, y: 0}
        targetLayer ??= 0
        targetMipLevel ??= 0

        this._drawCount++

        if (targetLayer < 0 || targetLayer >= this._descriptor.numLayers) {
            throw Error("Invalid layer.")
        }
        if (targetMipLevel < 0 || targetMipLevel >= this._descriptor.numMipLevels) {
            throw Error("Invalid mip level.")
        }
        if (targetOffset.x + sourceRegion.width > this._descriptor.width || targetOffset.y + sourceRegion.height > this._descriptor.height) {
            throw Error("Invalid region.")
        }

        const start = performance.now()

        if (isNativeImageData(sourceData) || isTypedArrayImage(sourceData)) {
            if (sourceMipLevel !== 0) {
                throw Error("Mip level must be 0 for writeImageData with native or typed-array image data.")
            }

            const gl = this.context.gl

            const {format: glFormat, type: glType} = getGlFormatAndType(gl, this._internalChannelLayout, this._internalDataType)

            let extractSourceRegion: (region: Box2Like) => Promise<TypedArrayImage> | Promise<NativeImageData>
            let sourceChannelLayout: HalImageChannelLayout
            let sourceDataType: HalImageDataType
            if (isNativeImageData(sourceData)) {
                sourceDataType = sourceData.isSrgb ? "uint8norm_srgb" : "uint8norm"
                const data = sourceData.data
                if (data instanceof HTMLImageElement || data instanceof HTMLCanvasElement || data instanceof ImageBitmap) {
                    sourceChannelLayout = "RGBA"
                    extractSourceRegion = async (region: Box2Like): Promise<NativeImageData> => {
                        if (region.x === 0 && region.y === 0 && region.width === data.width && region.height === data.height) {
                            return {
                                isSrgb: sourceData.isSrgb,
                                data: data,
                            }
                        }
                        if (isFirefox) {
                            // firefox has a bug where createImageBitmap does not respect the region offset so we need to go a detour via an offscreen canvas
                            function cropImageWithCanvas(
                                img: HTMLImageElement | HTMLCanvasElement | ImageBitmap,
                                x: number,
                                y: number,
                                width: number,
                                height: number,
                            ): Promise<ImageBitmap> {
                                const canvas = document.createElement("canvas")
                                canvas.width = width
                                canvas.height = height
                                const ctx = canvas.getContext("2d")
                                if (!ctx) throw new Error("Canvas context not available")
                                ctx.drawImage(img, x, y, width, height, 0, 0, width, height)
                                return createImageBitmap(canvas)
                            }
                            if (sourceData.data instanceof ImageData) {
                                throw Error("Cannot crop ImageData with canvas, use TypedArrayImage instead.")
                            }
                            const bitmap = await cropImageWithCanvas(sourceData.data, region.x, region.y, region.width, region.height)
                            return {
                                isSrgb: sourceData.isSrgb,
                                data: bitmap,
                            }
                        } else {
                            return {
                                isSrgb: sourceData.isSrgb,
                                data: await createCroppedImageBitmap(sourceData.data, region.x, region.y, region.width, region.height),
                            }
                        }
                        // const {canvas, ctx} = this.context.requestSynchronousRenderingContext2D(region.width, region.height)
                        // ctx.drawImage(data, region.x, region.y, region.width, region.height, 0, 0, region.width, region.height)
                        // return {
                        //     isSrgb: sourceData.isSrgb,
                        //     data: canvas,
                        // }
                        // return {
                        //     width: region.width,
                        //     height: region.height,
                        //     channelLayout: sourceChannelLayout,
                        //     data: ctx.getImageData(0, 0, region.width, region.height).data,
                        // }
                    }
                } else if (data instanceof ImageData) {
                    sourceChannelLayout = "RGBA"
                    extractSourceRegion = async (region: Box2Like): Promise<TypedArrayImage> => {
                        return {
                            width: region.width,
                            height: region.height,
                            channelLayout: sourceChannelLayout,
                            dataType: sourceDataType,
                            data: extractSubregionImageData(data, region).data,
                        }
                    }
                } else {
                    assertNever(data)
                }
            } else if (isTypedArrayImage(sourceData)) {
                const numChannels = getNumChannels(sourceData.channelLayout)
                if (sourceData.data.length !== sourceData.width * sourceData.height * numChannels) {
                    throw Error("Invalid image data")
                }
                sourceDataType = sourceData.dataType
                let arrayType: new (size: number) => Uint8ClampedArray | Uint8Array | Uint16Array | Float32Array
                if (sourceData.data instanceof Uint8ClampedArray) {
                    arrayType = Uint8ClampedArray
                } else if (sourceData.data instanceof Uint8Array) {
                    arrayType = Uint8Array
                } else if (sourceData.data instanceof Uint16Array) {
                    arrayType = Uint16Array
                } else if (sourceData.data instanceof Float32Array) {
                    arrayType = Float32Array
                } else {
                    assertNever(sourceData.data)
                }
                sourceChannelLayout = sourceData.channelLayout
                extractSourceRegion = async (region: Box2Like): Promise<TypedArrayImage> => {
                    const regionData = extractSubregionTypedArray(sourceData.data, arrayType, sourceData, numChannels, region)
                    return createTypedArrayImage({
                        width: region.width,
                        height: region.height,
                        channelLayout: sourceData.channelLayout,
                        dataType: sourceData.dataType,
                        data: regionData,
                    })
                }
            } else {
                assertNever(sourceData)
            }

            const {format: glSourceFormat, type: glSourceType} = getGlFormatAndType(gl, sourceChannelLayout, sourceDataType)

            const needsBlit = glFormat !== glSourceFormat || glType !== glSourceType
            const isImageBitmapSource =
                isNativeImageData(sourceData) &&
                (sourceData.data instanceof HTMLImageElement || sourceData.data instanceof HTMLCanvasElement || sourceData.data instanceof ImageBitmap)
            const useStripPath = isImageBitmapSource && !needsBlit && maxPixelsPerUpload !== undefined

            // move the data to the gpu
            const shardSize = Vector2.fromSize2Like(this._descriptor.shardSize)
            const minShard = Vector2.fromVector2Like(targetOffset).divInPlace(shardSize).floorInPlace()
            const maxShard = Vector2.fromVector2Like(targetOffset).addInPlace(Vector2.fromSize2Like(sourceRegion)).divInPlace(shardSize).ceilInPlace()
            const numShards = maxShard.sub(minShard)
            const targetRegion = new Box2(targetOffset.x, targetOffset.y, sourceRegion.width, sourceRegion.height)
            let frameStart = performance.now()
            for (let sy = 0; sy < numShards.y; sy++) {
                for (let sx = 0; sx < numShards.x; sx++) {
                    const shardIndex = (minShard.y + sy) * this._descriptor.numShards.x + minShard.x + sx
                    const layerIndex = this.computeLayerIndex(shardIndex, targetLayer)
                    const shardRegion = {
                        x: (minShard.x + sx) * shardSize.x,
                        y: (minShard.y + sy) * shardSize.y,
                        width: shardSize.x,
                        height: shardSize.y,
                    }
                    const intersectRegion = Box2.intersect(shardRegion, targetRegion)
                    const thisSourceRegion = {
                        x: intersectRegion.x - targetOffset.x + sourceRegion.x,
                        y: intersectRegion.y - targetOffset.y + sourceRegion.y,
                        width: intersectRegion.width,
                        height: intersectRegion.height,
                    }

                    if (useStripPath) {
                        const stripHeight = Math.max(1, Math.floor(maxPixelsPerUpload! / intersectRegion.width))
                        for (let row = 0; row < intersectRegion.height; row += stripHeight) {
                            const stripH = Math.min(stripHeight, intersectRegion.height - row)
                            const stripSrcRegion = {
                                x: thisSourceRegion.x,
                                y: thisSourceRegion.y + row,
                                width: intersectRegion.width,
                                height: stripH,
                            }
                            const regionData = (await extractSourceRegion(stripSrcRegion)) as NativeImageData
                            // rebind after every await — other GL ops may have changed the binding
                            gl.bindTexture(gl.TEXTURE_2D_ARRAY, this._texture)
                            gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
                            gl.texSubImage3D(
                                gl.TEXTURE_2D_ARRAY,
                                targetMipLevel,
                                intersectRegion.x - shardRegion.x,
                                intersectRegion.y - shardRegion.y + row,
                                layerIndex,
                                intersectRegion.width,
                                stripH,
                                1,
                                glFormat,
                                glType,
                                regionData.data,
                            )
                            checkForGlError(gl)
                            if (performance.now() - frameStart > FRAME_BUDGET_MS) {
                                await new Promise<void>((r) => setTimeout(r, 0))
                                frameStart = performance.now()
                            }
                        }
                        continue
                    }

                    const extractAndCopy = async (region: Box2Like) => {
                        const regionData = await extractSourceRegion(region)
                        // if the format does not match, use intermediate image and blit to the texture for conversion
                        const sourceShardImage = needsBlit
                            ? this.context.createPhysicalImage({
                                  width: Math.min(sourceRegion.width, this._descriptor.shardSize.width),
                                  height: Math.min(sourceRegion.height, this._descriptor.shardSize.height),
                                  channelLayout: sourceChannelLayout,
                                  dataType: sourceDataType,
                                  noRenderTarget: true,
                              })
                            : null
                        if (sourceShardImage) {
                            await sourceShardImage.ref.writeImageData({
                                sourceData: regionData,
                                sourceRegion: {
                                    x: 0,
                                    y: 0,
                                    width: intersectRegion.width,
                                    height: intersectRegion.height,
                                },
                            })
                            this.context.blit({
                                sourceImage: sourceShardImage.ref,
                                sourceRegion: {
                                    x: 0,
                                    y: 0,
                                    width: intersectRegion.width,
                                    height: intersectRegion.height,
                                },
                                targetImage: this,
                                targetOffset: {
                                    x: intersectRegion.x,
                                    y: intersectRegion.y,
                                },
                            })
                            sourceShardImage.release()
                        } else {
                            // this if/else is a bit stupid (same expression in if and else) but seems to be needed to please the typescript compiler
                            // const uploadTexture3DAsync = (
                            //     gl: WebGL2RenderingContext,
                            //     texture: WebGLTexture,
                            //     width: number,
                            //     height: number,
                            //     depth: number,
                            //     data: Uint8Array,
                            // ) => {
                            //     // Create and bind a Pixel Buffer Object (PBO)
                            //     const pbo = gl.createBuffer()
                            //     gl.bindBuffer(gl.PIXEL_UNPACK_BUFFER, pbo)
                            //
                            //     // Allocate GPU memory (without initializing)
                            //     gl.bufferData(gl.PIXEL_UNPACK_BUFFER, data.byteLength, gl.STREAM_DRAW)
                            //
                            //     // Upload data asynchronously using bufferSubData
                            //     gl.bufferSubData(gl.PIXEL_UNPACK_BUFFER, 0, data)
                            //
                            //     // Bind the texture and use the PBO for texture upload
                            //     gl.bindTexture(gl.TEXTURE_3D, texture)
                            //     gl.texSubImage3D(gl.TEXTURE_3D, 0, 0, 0, 0, width, height, depth, gl.RGBA, gl.UNSIGNED_BYTE, 0)
                            //
                            //     // Cleanup
                            //     gl.bindBuffer(gl.PIXEL_UNPACK_BUFFER, null)
                            //     gl.deleteBuffer(pbo)
                            // }
                            gl.bindTexture(gl.TEXTURE_2D_ARRAY, this._texture)
                            gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1) // make sure to tightly pack the data
                            // gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, 0)
                            // gl.pixelStorei(gl.UNPACK_SKIP_ROWS, 0)
                            // gl.pixelStorei(gl.UNPACK_ROW_LENGTH, 0)
                            // gl.pixelStorei(gl.UNPACK_IMAGE_HEIGHT, 0)
                            if (isNativeImageData(regionData)) {
                                gl.texSubImage3D(
                                    gl.TEXTURE_2D_ARRAY,
                                    targetMipLevel,
                                    intersectRegion.x - shardRegion.x,
                                    intersectRegion.y - shardRegion.y,
                                    layerIndex,
                                    intersectRegion.width,
                                    intersectRegion.height,
                                    1,
                                    glFormat,
                                    glType,
                                    regionData.data,
                                )
                            } else {
                                gl.texSubImage3D(
                                    gl.TEXTURE_2D_ARRAY,
                                    targetMipLevel,
                                    intersectRegion.x - shardRegion.x,
                                    intersectRegion.y - shardRegion.y,
                                    layerIndex,
                                    intersectRegion.width,
                                    intersectRegion.height,
                                    1,
                                    glFormat,
                                    glType,
                                    regionData.data,
                                )
                            }
                            checkForGlError(gl)
                        }
                    }
                    await extractAndCopy(thisSourceRegion)
                    if (performance.now() - frameStart > FRAME_BUDGET_MS) {
                        await new Promise<void>((r) => setTimeout(r, 0))
                        frameStart = performance.now()
                    }
                }
            }
        } else if (isHalImage(sourceData)) {
            this.context.blit({
                sourceImage: sourceData,
                sourceRegion: sourceRegion,
                sourceMipLevel: sourceMipLevel,
                targetImage: this,
                targetOffset: targetOffset,
                targetMipLevel: targetMipLevel,
            })
        } else {
            assertNever(sourceData)
        }

        const end = performance.now()
        logger.trace("Uploaded image to GPU in " + (end - start) + "ms")

        this._drawCount--
    }

    private createTexture(partialDescriptor: PartialHalImageDescriptor): {
        descriptor: WebGl2ImageDescriptor
        internalChannelLayout: HalImageChannelLayout
        internalDataType: HalImageDataType
        texture: WebGLTexture
        videoMemSizeInBytes: number
    } {
        const descriptor = completeHalImageDescriptor(partialDescriptor)

        if (descriptor.width <= 0 || descriptor.height <= 0) {
            throw Error("Image dimensions must be positive.")
        }
        if (!Number.isInteger(descriptor.width) || !Number.isInteger(descriptor.height)) {
            throw Error("Image dimensions must be integers.")
        }

        let internalChannelLayout = descriptor.channelLayout
        let internalDataType = descriptor.dataType
        if (!descriptor.noRenderTarget) {
            if (internalChannelLayout === "RGB") {
                // RGB format cannot be used as a render target (framebuffer color attachment) in WebGL2, so we
                // convert it to RGBA. Images marked noRenderTarget are never painted/blitted into, so we keep the
                // true RGB format - this matters for raw pixel uploads (writeImageData), which require an exact
                // format match between source data and internal storage.
                internalChannelLayout = "RGBA"
            }
            // EXT_color_buffer_float / EXT_color_buffer_half_float are only required to use a float texture as a
            // framebuffer attachment (render target). Core WebGL2 always supports float texture upload/sampling
            // without these extensions, so images marked noRenderTarget can keep their true requested dataType
            // and skip both the fallback and the "unsupported" throw below.
            switch (internalDataType) {
                case "float16":
                    if (!this.context.EXT_color_buffer_half_float) {
                        if (this.context.EXT_color_buffer_float) {
                            logger.warn("Device does not support float16 format. Falling back to float32.")
                            internalDataType = "float32"
                        } else {
                            throw new Error("Float format not supported by device")
                        }
                    }
                    break
                case "float32":
                    if (!this.context.EXT_color_buffer_float) {
                        if (this.context.EXT_color_buffer_half_float) {
                            logger.warn("Device does not support float32 format. Falling back to float16.")
                            internalDataType = "float16"
                        } else {
                            throw new Error("Float format not supported by device")
                        }
                    }
                    break
            }
        }

        const start = performance.now()

        const shardSize = new Size2(this.computeOptimalShardSize(descriptor.width), this.computeOptimalShardSize(descriptor.height))
        const numShards = new Vector2(Math.ceil(descriptor.width / shardSize.width), Math.ceil(descriptor.height / shardSize.height))
        const numLayers = descriptor.numLayers ?? 1
        const numMipLevels = Math.min(descriptor.numMipLevels, getNumMipLevels(shardSize))

        logger.trace(
            `Creating physical WebGL texture with ${numShards.x}x${numShards.y} shards of size ${shardSize.width}x${shardSize.height} containing ${numMipLevels} mipmap levels for image of size ${descriptor.width}x${descriptor.height}.`,
        )

        const bytesPerPixel = numLayers * numShards.x * numShards.y * getNumChannels(internalChannelLayout) * getBytesPerChannel(internalDataType)
        let videoMemSizeInBytes = 0
        for (let mipLevel = 0; mipLevel < numMipLevels; mipLevel++) {
            const size = getMipLevelSize(shardSize, mipLevel)
            videoMemSizeInBytes = bytesPerPixel * size.width * size.height
        }

        const gl = this.context.gl
        const texture = gl.createTexture()
        if (!texture) {
            throw new Error("Failed to create texture")
        }
        const currentBinding = gl.getParameter(gl.TEXTURE_BINDING_2D_ARRAY)
        gl.bindTexture(gl.TEXTURE_2D_ARRAY, texture)
        gl.texStorage3D(
            gl.TEXTURE_2D_ARRAY,
            numMipLevels,
            getInternalFormat(gl, internalChannelLayout, internalDataType),
            shardSize.width,
            shardSize.height,
            numShards.x * numShards.y * numLayers,
        )
        const lastError = gl.getError()
        if (lastError !== gl.NO_ERROR) {
            if (lastError === gl.OUT_OF_MEMORY) {
                throw new OutOfMemoryError("Failed to create texture: Out of memory.")
            } else {
                throw Error(`Failed to create texture (${lastError}).`)
            }
        }
        gl.bindTexture(gl.TEXTURE_2D_ARRAY, currentBinding) // restore previous binding

        const end = performance.now()
        logger.trace("Created physical GPU image in " + (end - start) + "ms")

        return {
            descriptor: {
                width: descriptor.width,
                height: descriptor.height,
                channelLayout: descriptor.channelLayout,
                dataType: descriptor.dataType,
                textureOffset: new Vector2(0, 0),
                wrapOffset: new Vector2(0, 0),
                wrapSize: new Size2(descriptor.width, descriptor.height),
                wrapGap: new Size2(0, 0),
                mipLevelOffset: 0,
                shardSize: shardSize,
                numShards: numShards,
                numLayers: numLayers,
                numMipLevels: numMipLevels,
            },
            internalChannelLayout,
            internalDataType,
            texture,
            videoMemSizeInBytes,
        }
    }

    prepareForUseAsSourceImage() {}

    private createFrameBuffers(descriptor: WebGl2ImageDescriptor, texture: WebGLTexture) {
        const frameBuffers: WebGLFramebuffer[] = []
        const numLayers = descriptor.numShards.x * descriptor.numShards.y * descriptor.numLayers
        if (numLayers > 0) {
            const gl = this.context.gl
            const currentBinding = gl.getParameter(gl.FRAMEBUFFER_BINDING)
            for (let mipLevel = 0; mipLevel < descriptor.numMipLevels; mipLevel++) {
                for (let layer = 0; layer < numLayers; layer++) {
                    const frameBuffer = gl.createFramebuffer()
                    if (!frameBuffer) {
                        throw new Error("Failed to create framebuffer")
                    }
                    frameBuffers.push(frameBuffer)
                    gl.bindFramebuffer(gl.FRAMEBUFFER, frameBuffer)
                    gl.framebufferTextureLayer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, texture, mipLevel, layer)
                }
            }
            gl.bindFramebuffer(gl.FRAMEBUFFER, currentBinding) // restore previous binding
        }
        return frameBuffers
    }

    private computeLayerIndex(shardIndex: number, layerIndex: number) {
        if (layerIndex < 0 || layerIndex >= this._descriptor.numLayers) {
            throw Error("Invalid layer index.")
        }
        if (shardIndex < 0 || shardIndex >= this._descriptor.numShards.x * this._descriptor.numShards.y) {
            throw Error("Invalid shard index.")
        }
        return layerIndex * this._descriptor.numShards.y * this._descriptor.numShards.x + shardIndex
    }

    private computeFrameBufferIndex(shardIndex: number, layerIndex: number, mipLevel: number) {
        if (mipLevel < 0 || mipLevel >= this._descriptor.numMipLevels) {
            throw Error("Invalid mip level.")
        }
        return (
            mipLevel * this._descriptor.numLayers * this._descriptor.numShards.y * this._descriptor.numShards.x + this.computeLayerIndex(shardIndex, layerIndex)
        )
    }

    private computeOptimalShardSize(length: number): number {
        // we want as little shards as possible, but we also don't want to waste too much unused space in the last shard
        const maxShardSize = this.context.maxTextureSize
        const maxNumShards = this.context.maxTextureLayers
        const numRequiredShards = Math.ceil(length / maxShardSize)
        if (numRequiredShards > maxNumShards) {
            throw Error(`Image would require more shards (${numRequiredShards}) than the GPU allows (${maxNumShards}).`)
        }
        return Math.ceil(length / numRequiredShards)
    }

    readonly stats: HalImageStats

    private _isDisposed = false
    private _descriptor: WebGl2ImageDescriptor
    private _internalChannelLayout: HalImageChannelLayout
    private _internalDataType: HalImageDataType
    private _texture: WebGLTexture
    private _frameBuffers: WebGLFramebuffer[]

    static instances = WebGl2Settings.InstanceTracking ? new Set<WebGl2ImagePhysical>() : undefined
}

export class OutOfMemoryError extends Error {
    constructor(message: string) {
        super(message)
        this.name = "OutOfMemoryError"
    }
}

const logger = loggerFactory.getLogger("webgl2")
